/**
 * End-to-end analysis pipeline: a parsed grid -> header detection -> column
 * profiling + matching (on clean data only) -> validated, keyed rows (junk and
 * bad rows surfaced, good rows ready). This is the single entrypoint the UI
 * calls after parsing; it does no I/O and no geocoding.
 */

import type { ColumnMapping, ColumnProfile, ParsedRow } from "./types";
import type { ParseGrid, RowCeilingResult } from "./parse";
import { checkRowCeiling } from "./parse";
import { buildColumnProfiles, classifyJunkRow, detectHeaderRow } from "./structure";
import { matchColumns } from "./columnMatch";
import { buildRows, summarize } from "./rows";

export const DEFAULT_COUNTRY = "US";

export interface IngestAnalysis {
  headerRowIndex: number;
  header: string[];
  profiles: ColumnProfile[];
  mapping: ColumnMapping;
  rows: ParsedRow[];
  countryDefault: string;
  summary: { ready: number; attention: number; skipped: number };
  rowCeiling: RowCeilingResult;
}

export function analyzeGrid(grid: ParseGrid, opts?: { countryDefault?: string }): IngestAnalysis {
  const countryDefault = opts?.countryDefault ?? DEFAULT_COUNTRY;
  const headerRowIndex = detectHeaderRow(grid.rows);
  const header = grid.rows[headerRowIndex] ?? [];
  const dataRows = grid.rows.slice(headerRowIndex + 1);

  // Profile + match on clean data only, so junk rows don't poison value evidence.
  const cleanDataRows = dataRows.filter((r) => classifyJunkRow(r) === null);
  const profiles = buildColumnProfiles(header, cleanDataRows);
  const mapping = matchColumns(profiles, cleanDataRows);

  // Build the full row set (all data rows) so junk/bad rows land in the tray.
  const rows = buildRows(mapping, dataRows);

  return {
    headerRowIndex,
    header,
    profiles,
    mapping,
    rows,
    countryDefault,
    summary: summarize(rows),
    rowCeiling: checkRowCeiling(dataRows.length),
  };
}
